/**
 * Top Sales Services Table Component
 *
 * A searchable table showing the top performing grooming services with ranking,
 * name, sales count, and total revenue. Uses V3 Table components for consistency.
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import ClickAwayListener from 'react-click-away-listener';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { RootState } from 'store';
import { useAuth } from 'hooks/useAuth';
import { useDebounce } from 'hooks/useDebounce';
import { fetchTopItemsThunk } from 'actions/salesDashboard/SalesDashboardActions';
import { TopItemsType } from 'model/salesDashboard';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import styles from './TopSalesServicesTable.module.scss';

export const TopSalesServicesTable: React.FC = React.memo(() => {
    const dispatch = useDispatch();
    const auth = useAuth();

    const { organizations } = useSelector((state: RootState) => state.organizations);
    const filters = useSelector((state: RootState) => state.salesDashboard.filters);
    const topItems = useSelector((state: RootState) => state.salesDashboard.topItems);
    const loadingTopItems = useSelector((state: RootState) => state.salesDashboard.loadingTopItems);

    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 500);
    const [selectedType, setSelectedType] = useState<TopItemsType>(TopItemsType.Services);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const getOrganizationId = (): number | undefined => {
        if (organizations && organizations.length > 0) {
            const parentOrg = organizations.find(org => org.parentOrganizationId == null);
            if (parentOrg) return parentOrg.id;
            return organizations[0].id;
        }
        if (auth?.user && (auth.user as unknown as Record<string, unknown>).organizationId) {
            return (auth.user as unknown as Record<string, unknown>).organizationId as number;
        }
        return undefined;
    };

    const organizationId = getOrganizationId();

    useEffect(() => {
        if (!organizationId || !filters) return;

        // Use any to bypass Redux thunk type issues
        dispatch(
            fetchTopItemsThunk(organizationId, {
                ...filters,
                type: selectedType,
                page: 1,
                perPage: 10,
                query: debouncedSearchText || undefined
            }) as any
        );
    }, [dispatch, organizationId, filters, selectedType, debouncedSearchText]);

    const typeOptions = [
        { id: TopItemsType.Services, name: 'Services' },
        { id: TopItemsType.Products, name: 'Products' },
        { id: TopItemsType.Addons, name: 'Add-ons' },
        { id: TopItemsType.CustomItems, name: 'Custom Items' }
    ];

    const selectedOption = typeOptions.find(opt => opt.id === selectedType);
    const currentItems = topItems?.items || [];

    return (
        <div className={styles.tableContainer}>
            {/* Table Header */}
            <div className={styles.tableHeader}>
                <div className={styles.headerContent}>
                    <h3 className={styles.tableTitle}>Top 10</h3>

                    <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
                        <div className={styles.dropdownContainer}>
                            <div
                                className={styles.dropdownToggle}
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                            >
                                <span className={styles.dropdownLabel}>{selectedOption?.name}</span>
                                <button className={styles.dropdownButton}>
                                    <FontAwesomeIcon
                                        icon={dropdownOpen ? faChevronUp : faChevronDown}
                                        className={styles.dropdownIcon}
                                    />
                                </button>
                            </div>

                            {dropdownOpen && (
                                <div className={styles.dropdownMenu}>
                                    {typeOptions.map(opt => (
                                        <div
                                            key={opt.id}
                                            className={`${styles.dropdownItem} ${
                                                selectedType === opt.id ? styles.selected : ''
                                            }`}
                                            onClick={() => {
                                                setSelectedType(opt.id);
                                                setDropdownOpen(false);
                                            }}
                                        >
                                            {opt.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </ClickAwayListener>
                </div>
            </div>

            {/* Search Bar */}
            <div className={styles.searchSection}>
                <SearchBar
                    searchText={searchText}
                    placeholder={`Filter ${selectedOption?.name || ''}`}
                    onChangeSearchText={setSearchText}
                    className={styles.searchBar}
                />
            </div>

            {/* Data Table */}
            <div className={styles.tableWrapper}>
                {loadingTopItems ? (
                    <div className={styles.loadingRows}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className={styles.skeletonRow}
                            >
                                <div className={`${styles.skeletonCell} ${styles.skeletonRanking}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonName}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonSales}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonSold}`} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        <Table className={styles.table}>
                            <TableHeader>
                                <TableHeaderCell className={styles.rankingColumn}>Ranking</TableHeaderCell>
                                <TableHeaderCell className={styles.nameColumn}>Name</TableHeaderCell>
                                <TableHeaderCell className={styles.salesColumn}>Sales</TableHeaderCell>
                                <TableHeaderCell className={styles.soldColumn}>Sold</TableHeaderCell>
                            </TableHeader>

                            <TableBody>
                                {currentItems.map(item => (
                                    <TableRow key={item.ranking}>
                                        <TableCell className={styles.rankingCell}>{item.ranking}</TableCell>
                                        <TableCell className={styles.nameCell}>
                                            {item.name.length > 20 ? (
                                                <Tooltip
                                                    content={item.name}
                                                    placement="top"
                                                >
                                                    <span className={styles.truncatedName}>
                                                        {item.name.slice(0, 20)}…
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                item.name
                                            )}
                                        </TableCell>
                                        <TableCell className={styles.salesCell}>{item.sales}</TableCell>
                                        <TableCell className={styles.soldCell}>
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                                minimumFractionDigits: 0
                                            }).format(item.sold)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {currentItems.length === 0 && (
                            <div className={styles.noResults}>
                                <p>No items found matching &rdquo;{searchText}&rdquo;</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
});

TopSalesServicesTable.displayName = 'TopSalesServicesTable';
